import React, { createContext, useContext, useState } from 'react';
import { ok } from 'node:assert/strict';
import type { FC, ReactNode } from 'react';
import type { WixAstroConfig } from '@wix/cli-astro-definitions';
import type { LooseBaseComponent } from '@wix/cli-app-manifest';
import { readWixConfig } from './files/wix.config.js';
import { loadDevCenterComponents } from './dev-center/components/load-dev-center-components.js';

export interface ProjectModel {
  config: WixAstroConfig;
  projectFolder: string;
  components: LooseBaseComponent[];
  releaseAppId: string;
}

export async function loadProjectModel(
  projectFolder: string
): Promise<ProjectModel> {
  const config = await readWixConfig(projectFolder);
  const components = await loadDevCenterComponents(projectFolder);

  return {
    projectFolder,
    config,
    components,
    releaseAppId:
      config.projectType === 'App' && config.projectId
        ? config.projectId
        : config.appId,
  };
}

interface ProjectModelState {
  model: ProjectModel;
}

const ProjectModelContext = createContext<ProjectModelState | null>(null);

interface Props {
  value: ProjectModel;
  children: ReactNode;
}

export const ProjectModelProvider: FC<Props> = (props) => {
  const [model] = useState(props.value);

  return (
    <ProjectModelContext.Provider value={{ model }}>
      {props.children}
    </ProjectModelContext.Provider>
  );
};

export function useProjectModel(): ProjectModelState {
  const context = useContext(ProjectModelContext);
  if (context == null) {
    throw new Error(
      'useProjectModel() cannot be used without a <ProjectModelProvider>'
    );
  }
  return context;
}

export function useSiteConfig() {
  const {
    model: { config },
  } = useProjectModel();

  ok(config.projectType === 'Site', 'Project is not a Site');

  return config;
}
